import { supabase } from '../config/supabase';
import { Message, SendMessageInput } from '../types';
import { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Messages Service
 * Manages chat message operations with real-time Supabase subscriptions
 */
export const messagesService = {
  /**
   * Send a message in a conversation
   * @param conversationId - The ID of the conversation
   * @param content - The message content (text)
   * @param type - Message type (default: 'text')
   * @param metadata - Optional metadata for system/price_proposal messages
   * @returns Promise<Message> The created message with sender profile
   * @throws Error if user is not authenticated or send fails
   */
  async sendMessage(
    conversationId: string,
    content: string,
    type: 'text' | 'system' | 'price_proposal' = 'text',
    metadata?: Record<string, any>
  ): Promise<Message> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const messageData: SendMessageInput = {
      conversation_id: conversationId,
      content,
      message_type: type,
      metadata,
    };

    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: messageData.conversation_id,
        sender_id: user.id,
        content: messageData.content,
        message_type: messageData.message_type,
        metadata: messageData.metadata,
      })
      .select('*, sender:profiles(*)')
      .single();

    if (error) throw error;

    return data;
  },

  /**
   * Get all messages for a conversation
   * @param conversationId - The ID of the conversation
   * @param limit - Maximum number of messages to retrieve (default: 100)
   * @param offset - Pagination offset (default: 0)
   * @returns Promise<Message[]> Array of messages with sender profiles
   * @throws Error if query fails
   */
  async getMessages(
    conversationId: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<Message[]> {
    const { data, error } = await supabase
      .from('messages')
      .select('*, sender:profiles(*)')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    return data || [];
  },

  /**
   * Mark a message as read
   * Sets read_at timestamp to current time
   * @param messageId - The ID of the message to mark as read
   * @returns Promise<void>
   * @throws Error if update fails
   */
  async markAsRead(messageId: string): Promise<void> {
    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('id', messageId);

    if (error) throw error;
  },

  /**
   * Mark multiple messages as read
   * Batch operation for marking all unread messages in a conversation
   * @param messageIds - Array of message IDs to mark as read
   * @returns Promise<void>
   * @throws Error if update fails
   */
  async markMultipleAsRead(messageIds: string[]): Promise<void> {
    if (messageIds.length === 0) return;

    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .in('id', messageIds);

    if (error) throw error;
  },

  /**
   * Mark all messages in a conversation as read
   * Convenience method for marking entire conversation as read
   * @param conversationId - The ID of the conversation
   * @returns Promise<void>
   * @throws Error if query or update fails
   */
  async markConversationAsRead(conversationId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Only mark messages as read where:
    // 1. They belong to this conversation
    // 2. They're not sent by current user (don't mark own messages)
    // 3. They haven't been read yet
    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('conversation_id', conversationId)
      .neq('sender_id', user.id)
      .is('read_at', null);

    if (error) throw error;
  },

  /**
   * Get unread message count for a conversation
   * @param conversationId - The ID of the conversation
   * @returns Promise<number> Count of unread messages
   * @throws Error if query fails
   */
  async getUnreadCount(conversationId: string): Promise<number> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('conversation_id', conversationId)
      .neq('sender_id', user.id) // Don't count own messages
      .is('read_at', null);

    if (error) throw error;

    return count || 0;
  },

  /**
   * Subscribe to new messages in a conversation (Real-time)
   * @param conversationId - The ID of the conversation to subscribe to
   * @param callback - Function called when new message is received
   * @returns RealtimeChannel The subscription channel (call .unsubscribe() to stop)
   *
   * @example
   * const subscription = messagesService.subscribeToMessages(
   *   conversationId,
   *   (message) => {
   *     console.log('New message:', message);
   *     setMessages(prev => [...prev, message]);
   *   }
   * );
   *
   * // Later, cleanup
   * subscription.unsubscribe();
   */
  subscribeToMessages(
    conversationId: string,
    callback: (message: Message) => void
  ): RealtimeChannel {
    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          // Payload contains the new message but without joined sender data
          // Fetch the full message with sender profile
          const { data, error } = await supabase
            .from('messages')
            .select('*, sender:profiles(*)')
            .eq('id', payload.new.id)
            .single();

          if (!error && data) {
            callback(data);
          }
        }
      )
      .subscribe();

    return channel;
  },

  /**
   * Subscribe to message updates (e.g., when read_at changes)
   * @param conversationId - The ID of the conversation to subscribe to
   * @param callback - Function called when message is updated
   * @returns RealtimeChannel The subscription channel
   *
   * @example
   * const subscription = messagesService.subscribeToMessageUpdates(
   *   conversationId,
   *   (message) => {
   *     console.log('Message updated:', message);
   *     updateMessageInList(message);
   *   }
   * );
   */
  subscribeToMessageUpdates(
    conversationId: string,
    callback: (message: Message) => void
  ): RealtimeChannel {
    const channel = supabase
      .channel(`message-updates:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          // Fetch the full updated message with sender profile
          const { data, error } = await supabase
            .from('messages')
            .select('*, sender:profiles(*)')
            .eq('id', payload.new.id)
            .single();

          if (!error && data) {
            callback(data);
          }
        }
      )
      .subscribe();

    return channel;
  },

  /**
   * Delete a message (if allowed by business rules)
   * Note: Current RLS policies don't allow DELETE, this is for future use
   * @param messageId - The ID of the message to delete
   * @returns Promise<void>
   * @throws Error if deletion fails or not permitted
   */
  async deleteMessage(messageId: string): Promise<void> {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);

    if (error) throw error;
  },

  /**
   * Send a system message (for automated notifications)
   * System messages are typically generated by the app, not user-typed
   * @param conversationId - The ID of the conversation
   * @param content - The system message content
   * @param metadata - Optional metadata (e.g., transaction_id, event_type)
   * @returns Promise<Message> The created system message
   * @throws Error if send fails
   */
  async sendSystemMessage(
    conversationId: string,
    content: string,
    metadata?: Record<string, any>
  ): Promise<Message> {
    return this.sendMessage(conversationId, content, 'system', metadata);
  },

  /**
   * Get total unread message count across all user's conversations
   * Used for navigation badge
   * @returns Promise<number> Total unread count
   */
  async getTotalUnreadCount(): Promise<number> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return 0;

    // Get all conversations user is part of
    const { data: conversations } = await supabase
      .from('conversations')
      .select('id')
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);

    if (!conversations || conversations.length === 0) return 0;

    // Count unread messages in those conversations
    const conversationIds = conversations.map(c => c.id);

    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .in('conversation_id', conversationIds)
      .neq('sender_id', user.id)  // Don't count own messages
      .is('read_at', null);

    if (error) throw error;

    return count || 0;
  },

  /**
   * Mark all unread messages in a conversation as read
   * Called when user views ChatScreen
   * @param conversationId - The conversation ID
   * @returns Promise<void>
   */
  async markConversationMessagesAsRead(conversationId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('conversation_id', conversationId)
      .neq('sender_id', user.id)  // Don't mark own messages
      .is('read_at', null);

    if (error) throw error;
  },
};
